import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
} from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { tap, share } from 'rxjs/operators';

interface CacheEntry {
  response: HttpEvent<any>;
  timestamp: number;
}

@Injectable()
export class RequestCacheInterceptor implements HttpInterceptor {
  private cache = new Map<string, Observable<HttpEvent<any>>>();
  private responseCache = new Map<string, CacheEntry>();
  private readonly CACHE_DURATION = 30 * 1000;

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (req.method !== 'GET') {
      return next.handle(req);
    }

    const cachableEndpoints = [
      '/unread-count',
      '/summary',
      '/featured',
      '/recent',
      '/health',
      '/wishlist/check',
      '/wishlist/summary',
    ];

    const isCachable = cachableEndpoints.some((endpoint) =>
      req.url.includes(endpoint)
    );

    if (!isCachable) {
      return next.handle(req);
    }

    const cacheKey = `${req.method}-${req.url}`;

    const inFlightRequest = this.cache.get(cacheKey);
    if (inFlightRequest) {
      return inFlightRequest;
    }

    const cachedResponse = this.responseCache.get(cacheKey);
    if (
      cachedResponse &&
      Date.now() - cachedResponse.timestamp < this.CACHE_DURATION
    ) {
      return of(cachedResponse.response);
    }

    const request$ = next.handle(req).pipe(
      tap((event) => {
        this.responseCache.set(cacheKey, {
          response: event,
          timestamp: Date.now(),
        });
      }),
      share()
    );

    this.cache.set(cacheKey, request$);

    request$.subscribe({
      complete: () => {
        this.cache.delete(cacheKey);
      },
      error: () => {
        this.cache.delete(cacheKey);
      },
    });

    return request$;
  }

  clearCache(): void {
    this.cache.clear();
    this.responseCache.clear();
  }
}
